import { useState } from 'react';
import {
  useListSchedulerEventsQuery,
  useGetSchedulerEventQuery,
  useCancelSchedulerEventMutation,
  useSyncSchedulerEventsMutation,
  type SchedulerEvent,
} from '@/redux/internal-services/schedulerEventsApi';

const STATUSES = ['active', 'fired', 'cancelled', 'deleted', 'failed'] as const;

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-blue-100 text-blue-700 border-blue-200',
    fired: 'bg-green-100 text-green-700 border-green-200',
    cancelled: 'bg-ui-subtle text-ui-text2 border-ui-border',
    deleted: 'bg-ui-subtle text-ui-text2 border-ui-border',
    failed: 'bg-red-100 text-red-700 border-red-200',
  };
  const cls = map[status] ?? 'bg-ui-subtle text-ui-text2 border-ui-border';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border capitalize ${cls}`}>
      {status}
    </span>
  );
}

export default function SchedulerEventsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [skip, setSkip] = useState(0);
  const [syncSummary, setSyncSummary] = useState<string | null>(null);
  const limit = 25;

  const { data, isFetching, refetch } = useListSchedulerEventsQuery({
    status: statusFilter || undefined,
    event_type: eventTypeFilter || undefined,
    tenant_id: tenantFilter || undefined,
    limit,
    skip,
  });
  const { data: detail } = useGetSchedulerEventQuery(selectedName!, { skip: !selectedName });

  const [cancelEvent, { isLoading: isCancelling }] = useCancelSchedulerEventMutation();
  const [syncEvents, { isLoading: isSyncing }] = useSyncSchedulerEventsMutation();

  const events = data?.data ?? [];
  const total = data?.total ?? 0;

  const handleCancel = async (scheduleName: string) => {
    if (!window.confirm(`Cancel trigger "${scheduleName}"? This removes it from EventBridge and cannot be undone.`)) {
      return;
    }
    try {
      await cancelEvent(scheduleName).unwrap();
    } catch {
      // error surfaced via row still showing active status
    }
  };

  const handleSync = async () => {
    setSyncSummary(null);
    try {
      const res = await syncEvents().unwrap();
      const { eventbridge_active_count, local_active_count, orphaned_marked_deleted } = res.data;
      setSyncSummary(
        `EventBridge: ${eventbridge_active_count} active · Local: ${local_active_count} active` +
          (orphaned_marked_deleted.length ? ` · ${orphaned_marked_deleted.length} orphaned record(s) marked deleted` : ' · in sync'),
      );
    } catch {
      setSyncSummary('Sync failed — check server logs.');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-ui-raised">
      <div className="border-b border-ui-border bg-ui-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-ui-text">Scheduled Triggers</h1>
            <p className="text-xs text-ui-text2 mt-0.5">
              Track every EventBridge schedule the system has created — reminders, recurring jobs — and cancel active triggers.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="rounded-lg border border-ui-border px-3 py-1.5 text-xs font-medium text-ui-text2 hover:bg-ui-hover disabled:opacity-40"
            >
              {isSyncing ? 'Syncing…' : 'Sync with EventBridge'}
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="rounded-lg border border-ui-border px-3 py-1.5 text-xs font-medium text-ui-text2 hover:bg-ui-hover disabled:opacity-40"
            >
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
        {syncSummary && <p className="mt-2 text-xs text-ui-text2">{syncSummary}</p>}
      </div>

      <div className="flex-1 px-6 py-5 flex flex-col gap-4">
        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
            className="rounded border border-gray-300 bg-ui-card px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s} className="capitalize">{s}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Filter by event type..."
            value={eventTypeFilter}
            onChange={(e) => { setEventTypeFilter(e.target.value); setSkip(0); }}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Filter by tenant ID..."
            value={tenantFilter}
            onChange={(e) => { setTenantFilter(e.target.value); setSkip(0); }}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-xs text-ui-text3">{total} results</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-ui-border bg-ui-card">
          <table className="min-w-full text-sm">
            <thead className="bg-ui-raised text-xs text-ui-text2 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Schedule Name</th>
                <th className="px-4 py-3 text-left">Event Type</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Tenant</th>
                <th className="px-4 py-3 text-left">Fires At</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ui-border2">
              {isFetching && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ui-text3">Loading...</td></tr>
              )}
              {!isFetching && events.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ui-text3">No scheduled triggers found.</td></tr>
              )}
              {!isFetching && events.map((e: SchedulerEvent) => (
                <tr key={e.schedule_name} className="hover:bg-ui-hover">
                  <td className="px-4 py-3 font-mono text-xs text-ui-text">{e.schedule_name}</td>
                  <td className="px-4 py-3 text-xs text-ui-text2">{e.event_type}</td>
                  <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                  <td className="px-4 py-3 text-xs text-ui-text3">{e.tenant_id || '—'}</td>
                  <td className="px-4 py-3 text-xs text-ui-text2">
                    {e.fire_at ? new Date(e.fire_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-ui-text3">
                    {e.created_on ? new Date(e.created_on).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedName(e.schedule_name === selectedName ? null : e.schedule_name)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Detail
                      </button>
                      {e.status === 'active' && (
                        <button
                          onClick={() => handleCancel(e.schedule_name)}
                          disabled={isCancelling}
                          className="text-xs text-red-500 hover:underline disabled:opacity-40"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between text-xs text-ui-text2">
            <span>Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
            <div className="flex gap-2">
              <button
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - limit))}
                className="rounded border px-3 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                disabled={skip + limit >= total}
                onClick={() => setSkip(skip + limit)}
                className="rounded border px-3 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Detail panel */}
        {detail?.data && (
          <div className="rounded-lg border border-ui-border bg-ui-card p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-ui-text">Trigger Detail</p>
              <button onClick={() => setSelectedName(null)} className="text-ui-text3 hover:text-ui-text2">
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z"/>
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-ui-text2">
              <div><span className="font-medium">Schedule name:</span> {detail.data.schedule_name}</div>
              <div><span className="font-medium">Group:</span> {detail.data.group_name}</div>
              <div><span className="font-medium">Source ID:</span> {detail.data.source_id || '—'}</div>
              <div><span className="font-medium">Fired on:</span> {detail.data.fired_on ? new Date(detail.data.fired_on).toLocaleString() : '—'}</div>
              <div><span className="font-medium">Deleted on:</span> {detail.data.deleted_on ? new Date(detail.data.deleted_on).toLocaleString() : '—'}</div>
              <div><span className="font-medium">Updated:</span> {detail.data.updated_on ? new Date(detail.data.updated_on).toLocaleString() : '—'}</div>
            </div>
            {detail.data.failure_reason && (
              <p className="mt-2 rounded bg-red-50 px-3 py-2 text-xs text-red-700 font-mono">{detail.data.failure_reason}</p>
            )}
            {detail.data.payload && (
              <pre className="mt-3 rounded bg-ui-raised px-3 py-2 text-xs text-ui-text2 overflow-x-auto">
                {JSON.stringify(detail.data.payload, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
